/**
 * Fragment nazwy pliku zgodny z legacy `public/js/download.js`.
 */
export function convertToSlug(text: string): string {
  return text.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
}
export type CreativeImageFace = 'main' | 'inner' | 'back';

/**
 * Konfigurator bywa zwracał `main` / `inner` jako string (data URL) albo jako obiekt
 * (`{ CART, FULL }` albo pełny `{ main, inner, back }` w jednym polu).
 *
 * `face` — które pole traktować jako docelowy URL, gdy `raw` jest „tripletem”.
 */
export function resolveCreativeImageUrl(raw: unknown, face: CreativeImageFace = 'main', depth = 0): string {
  if (depth > 6) {
    throw new TypeError('resolveCreativeImageUrl: zbyt głębokie zagnieżdżenie');
  }
  if (typeof raw === 'string') {
    return raw;
  }
  if (!raw || typeof raw !== 'object') {
    throw new TypeError(`resolveCreativeImageUrl: oczekiwano stringa lub obiektu z URL, otrzymano ${raw === null ? 'null' : typeof raw}`);
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.FULL === 'string') {
    return o.FULL;
  }
  if (typeof o.CART === 'string') {
    return o.CART;
  }
  if (typeof o.url === 'string') {
    return o.url;
  }
  const mainInnerBack = ['main', 'inner', 'back'] as const;
  const looksLikeTriplet = mainInnerBack.every(k => typeof o[k] === 'string');
  if (looksLikeTriplet) {
    return o[face] as string;
  }
  const direct = o[face];
  if (typeof direct === 'string') {
    return direct;
  }
  if (o.main !== undefined && typeof o.main !== 'string') {
    return resolveCreativeImageUrl(o.main, face, depth + 1);
  }
  if (typeof o.main === 'string') {
    return o.main;
  }
  throw new TypeError('resolveCreativeImageUrl: nieznany kształt obiektu obrazu');
}
async function stringSourceToBlob(source: string, fallbackMime: string): Promise<Blob> {
  if (source.startsWith('data:')) {
    const response = await fetch(source);
    if (!response.ok) {
      throw new Error(`downloadFile: failed to read data URL (${response.status})`);
    }
    return response.blob();
  }
  if (/^https?:\/\//i.test(source) || source.startsWith('/') || source.startsWith('//')) {
    const response = await fetch(source);
    if (!response.ok) {
      throw new Error(`downloadFile: failed to fetch resource (${response.status})`);
    }
    return response.blob();
  }
  return new Blob([source], {
    type: fallbackMime
  });
}
function saveBlob(blob: Blob, fileName: string, fallbackMime: string): void {
  const needsMime = !blob.type || blob.type === 'application/octet-stream' || blob.type === '';
  const finalBlob = needsMime ? new Blob([blob], {
    type: fallbackMime
  }) : blob;
  const objectUrl = URL.createObjectURL(finalBlob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.setAttribute('download', fileName);
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 250);
}
export type DownloadFileOptions = {
  /** Które pole tripletu wybrać, gdy `source` jest obiektem `{ main, inner, back }`. */
  imageFace?: CreativeImageFace;
};

/**
 * Zapis w przeglądarce (data URL, http(s), ścieżka względna lub Blob).
 * `source` może być surowym stringiem z konfiguratora albo obiektem `{ FULL, CART }` / zagnieżdżonym `{ main }`.
 */
export async function downloadFile(source: string | Blob | unknown, fileName: string, mimeType = 'application/octet-stream', options?: DownloadFileOptions): Promise<void> {
  if (source instanceof Blob) {
    saveBlob(source, fileName, mimeType);
    return;
  }
  const face = options?.imageFace ?? 'main';
  const urlString = resolveCreativeImageUrl(source, face);
  const blob = await stringSourceToBlob(urlString, mimeType);
  saveBlob(blob, fileName, mimeType);
}